import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
    SKELETON_BAR_GAP,
    SKELETON_BAR_WIDTH,
    SKELETON_HEIGHT,
    generateSkeletonBarHeights,
    getSkeletonBarLoadedStates,
    skeletonUnloadedColor,
    skeletonWaveColor,
} from "./waveformSkeleton";

interface WaveformLoadSkeletonProps {
    bufferPercent: number;
    seed: string;
    className?: string;
    /** Fixed bar count for tests; otherwise derived from container width. */
    barCountOverride?: number;
}

export function WaveformLoadSkeleton({
    bufferPercent,
    seed,
    className,
    barCountOverride,
}: WaveformLoadSkeletonProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [measuredBarCount, setMeasuredBarCount] = useState(0);

    useLayoutEffect(() => {
        if (barCountOverride != null) return;
        const element = containerRef.current;
        if (!element) return;

        const updateBarCount = () => {
            const width = element.clientWidth;
            setMeasuredBarCount(
                Math.max(1, Math.floor(width / (SKELETON_BAR_WIDTH + SKELETON_BAR_GAP))),
            );
        };

        updateBarCount();
        const observer = new ResizeObserver(updateBarCount);
        observer.observe(element);
        return () => observer.disconnect();
    }, [barCountOverride]);

    const barCount = barCountOverride ?? measuredBarCount;
    const heights = useMemo(
        () => generateSkeletonBarHeights(seed, barCount),
        [seed, barCount],
    );
    const loadedStates = useMemo(
        () => getSkeletonBarLoadedStates(barCount, bufferPercent),
        [barCount, bufferPercent],
    );

    return (
        <div
            ref={containerRef}
            className={cn("flex w-full items-center", className)}
            style={{ height: SKELETON_HEIGHT, gap: SKELETON_BAR_GAP }}
            aria-hidden
        >
            {heights.map((height, index) => (
                <div
                    key={index}
                    data-loaded={loadedStates[index] ? "true" : "false"}
                    style={{
                        width: SKELETON_BAR_WIDTH,
                        height,
                        borderRadius: 3,
                        backgroundColor: loadedStates[index]
                            ? skeletonWaveColor()
                            : skeletonUnloadedColor(),
                        flexShrink: 0,
                        transition: "background-color 150ms linear",
                    }}
                />
            ))}
        </div>
    );
}
